/**
 * `useAuthz()` — frontend hook over `/api/v1/auth/me` (W21-F1-S6).
 *
 * Restores the legacy `$this->Authorization->can($action, $entity)` ergonomics
 * (`dd9b1eeb^:src/Template/Element/Users/managers_header.ctp`) in React form.
 * Fetches the per-session capability map once via TanStack Query and exposes:
 *
 *   - `senviroRoles`   — array of roles the user holds (mapped from Core)
 *   - `hasRole(role)`  — boolean role check
 *   - `can(action, entity)` — per-entity capability lookup; consults the
 *     capability map returned by the backend, with a permissive fallback for
 *     `view` on entities not in the registry (matches legacy CakePHP)
 *
 * Cache policy: `staleTime: Infinity`, no refetch on window focus. Role
 * changes mid-session take effect on next page reload — explicit invalidation
 * via `queryClient.invalidateQueries(['authz'])` is also supported.
 */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';

export type SenviroRole =
  | 'manager'
  | 'consultant'
  | 'agent'
  | 'partner_admin'
  | 'customer_admin'
  | 'platform_admin'
  | 'lp_viewer';

export type AuthzAction = 'view' | 'create' | 'edit' | 'delete';

export type AuthzEntity =
  | 'asset'
  | 'portfolio'
  | 'assetHolder'
  | 'translation'
  | 'module'
  | 'module_activity'
  | 'module_template'
  | 'breeam_template'
  | 'recurring_variable'
  | 'user'
  | 'feature_flag'
  | 'report_layout'
  | 'scorer_config'
  | 'admin_setting';

interface EntityCaps {
  view: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
}

export interface AuthzMeResponse {
  userId: string;
  tenantId: string;
  senviroRoles: SenviroRole[];
  capabilities: {
    asset: EntityCaps;
    portfolio: EntityCaps;
    assetHolder: EntityCaps;
    admin: Record<string, EntityCaps>;
  };
}

const AUTHZ_QUERY_KEY = ['authz', 'me'] as const;

const EMPTY_AUTHZ: AuthzMeResponse = {
  userId: '',
  tenantId: '',
  senviroRoles: [],
  capabilities: {
    asset: { view: false, create: false, edit: false, delete: false },
    portfolio: { view: false, create: false, edit: false, delete: false },
    assetHolder: { view: false, create: false, edit: false, delete: false },
    admin: {},
  },
};

export interface UseAuthzResult {
  isLoading: boolean;
  data: AuthzMeResponse;
  senviroRoles: SenviroRole[];
  hasRole: (role: SenviroRole) => boolean;
  can: (action: AuthzAction, entity: AuthzEntity) => boolean;
}

export function useAuthz(): UseAuthzResult {
  const { getAccessToken } = useAuth();

  const query = useQuery<AuthzMeResponse>({
    queryKey: AUTHZ_QUERY_KEY,
    queryFn: () =>
      apiFetch<AuthzMeResponse>('/api/v1/auth/me', { getAccessToken }),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const data = query.data ?? EMPTY_AUTHZ;
  const senviroRoles = data.senviroRoles;

  const hasRole = useCallback(
    (role: SenviroRole) => senviroRoles.includes(role),
    [senviroRoles],
  );

  const can = useCallback(
    (action: AuthzAction, entity: AuthzEntity): boolean => {
      // platform_admin always wins — defensive guard in case the server map
      // ever lags behind the role list.
      if (senviroRoles.includes('platform_admin')) return true;

      const caps = data.capabilities;
      if (entity === 'asset') return caps.asset[action];
      if (entity === 'portfolio') return caps.portfolio[action];
      if (entity === 'assetHolder') return caps.assetHolder[action];

      const adminEntry = caps.admin[entity];
      if (adminEntry) return adminEntry[action];

      // Unknown / not-yet-registered entity — conservative default: only
      // `view` defaults to true (matches legacy permissive read).
      return action === 'view';
    },
    [data, senviroRoles],
  );

  return useMemo(
    () => ({
      isLoading: query.isLoading,
      data,
      senviroRoles,
      hasRole,
      can,
    }),
    [query.isLoading, data, senviroRoles, hasRole, can],
  );
}

export const __testing__ = {
  AUTHZ_QUERY_KEY,
  EMPTY_AUTHZ,
};
